'use client'

import CommunityCard from '@/components/_cards/community/community-card'
import MagnifyingGlassIcon from '@/components/icons/magnifying-glass'
import { Accordion } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { dayOptions } from '@/helpers/days'
import { buildSearchParamsObject } from '@/lib/server/sim-racing-games/schemas'
import { cn } from '@/lib/utils'
import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { loadMoreCommunities } from '../../actions/load-more'
import FilterCheckbox from '../../components/checkbox'
import Filter from '../../components/filter'
import MobileFilterWrapper, {
  AccordionItemWrapper,
} from '../../components/mobile-filter-wrapper'

export default function CommunitiesClientPage({
  game,
  gameName,
  appliedFilter,
  initialItems,
  initialTotal,
  initialNextCursor,
}) {
  const pathname = usePathname()
  const router = useRouter()
  const loaderRef = useRef(null)

  const [items, setItems] = useState(initialItems)
  const [total, setTotal] = useState(initialTotal)
  const [nextCursor, setNextCursor] = useState(initialNextCursor)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [draft, setDraft] = useState(appliedFilter)

  const platformOptions = useMemo(
    () => game.platforms.map((entry) => entry.platform),
    [game.platforms],
  )

  useEffect(() => {
    setItems(initialItems)
    setTotal(initialTotal)
    setNextCursor(initialNextCursor)
    setDraft(appliedFilter)
  }, [appliedFilter, initialItems, initialNextCursor, initialTotal])

  const hasMore = Boolean(nextCursor)

  const applyDraft = useCallback(() => {
    const params = buildSearchParamsObject(draft)
    const query = params.toString()
    router.push(query ? `${pathname}?${query}` : pathname)
  }, [draft, pathname, router])

  const clearDraft = useCallback(() => {
    const reset = {
      q: '',
      platforms: [],
      days: [],
      cursor: undefined,
    }

    setDraft(reset)
    router.push(pathname)
  }, [pathname, router])

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) return

    setIsLoadingMore(true)

    try {
      const result = await loadMoreCommunities({
        gameName,
        filter: appliedFilter,
        cursor: nextCursor,
      })

      setItems((previous) => [...previous, ...result.items])
      setTotal(result.total)
      setNextCursor(result.nextCursor)
    } finally {
      setIsLoadingMore(false)
    }
  }, [appliedFilter, gameName, isLoadingMore, nextCursor])

  useEffect(() => {
    if (!loaderRef.current || !hasMore) return

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore()
      },
      { threshold: 0.2 },
    )

    observer.observe(loaderRef.current)

    return () => observer.disconnect()
  }, [hasMore, loadMore])

  return (
    <>
      <MobileFilterWrapper onClear={clearDraft} onApply={applyDraft}>
        <Accordion type="single" collapsible className="w-full">
          {platformOptions.length > 1 && (
            <AccordionItemWrapper value="platforms" title="Platforms">
              {platformOptions.map((platform) => (
                <FilterCheckbox
                  key={platform.name}
                  checked={draft.platforms.includes(platform.name)}
                  label={platform.nicename}
                  onChange={() => {
                    setDraft((previous) => ({
                      ...previous,
                      platforms: previous.platforms.includes(platform.name)
                        ? previous.platforms.filter(
                            (value) => value !== platform.name,
                          )
                        : [...previous.platforms, platform.name],
                    }))
                  }}
                />
              ))}
            </AccordionItemWrapper>
          )}

          <AccordionItemWrapper value="days" title="Days">
            {dayOptions.map((day) => (
              <FilterCheckbox
                key={day.name}
                checked={draft.days.includes(day.name)}
                label={day.nicename}
                onChange={() => {
                  setDraft((previous) => ({
                    ...previous,
                    days: previous.days.includes(day.name)
                      ? previous.days.filter((value) => value !== day.name)
                      : [...previous.days, day.name],
                  }))
                }}
              />
            ))}
          </AccordionItemWrapper>
        </Accordion>
      </MobileFilterWrapper>

      <div className="col-span-full mt-6 hidden h-[46px] items-center justify-between desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:flex">
        <div className="flex items-center gap-3">
          <p className="text-p-1 text-neutral-blue-500">Filter</p>
          {platformOptions.length > 1 && (
            <Filter
              value={draft.platforms}
              setValue={(platforms) =>
                setDraft((previous) => ({ ...previous, platforms }))
              }
              options={platformOptions}
              displayLabel="nicename"
              valueLabel="name"
              description="Platforms"
              parentClassName="w-[200px]"
              displayClassName="w-[200px]"
              multi
            />
          )}
          <Filter
            value={draft.days}
            setValue={(days) => setDraft((previous) => ({ ...previous, days }))}
            options={dayOptions}
            displayLabel="nicename"
            valueLabel="name"
            description="Days"
            parentClassName="w-[200px]"
            displayClassName="w-[200px]"
            multi
          />
          <Button variant="secondary" onClick={clearDraft}>
            Clear
          </Button>
          <Button onClick={applyDraft}>Apply</Button>
        </div>

        <div className="relative flex w-[392px] items-center">
          <Input
            className="pl-12"
            placeholder="Search Communities"
            value={draft.q}
            onChange={(event) =>
              setDraft((previous) => ({ ...previous, q: event.target.value }))
            }
            onKeyDown={(event) => {
              if (event.key === 'Enter') applyDraft()
            }}
          />
          <MagnifyingGlassIcon className="pointer-events-none absolute ml-6 h-4 w-4 stroke-shades-0 stroke-2" />
        </div>
      </div>

      <div className="col-span-full mt-6 grid gap-5 desktop-xs:col-span-10 desktop-xs:col-start-2 desktop-xs:grid-cols-2 desktop-sm:grid-cols-3 desktop-lg:gap-9">
        {items.length > 0 ? (
          items.map((community) => (
            <div key={community.id}>
              <CommunityCard community={community} />
            </div>
          ))
        ) : (
          <p className="col-span-full">No communities found.</p>
        )}

        {hasMore && (
          <div
            ref={loaderRef}
            className="col-span-full flex justify-center py-4"
          >
            {isLoadingMore ? (
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-blue-700 border-t-primary-500" />
            ) : (
              <Button variant="secondary" onClick={loadMore}>
                Load more
              </Button>
            )}
          </div>
        )}
      </div>

      <p
        className={cn(
          'col-span-full mt-6 text-right text-p-2 text-neutral-blue-300 desktop-xs:col-span-10 desktop-xs:col-start-2',
          total === 0 && 'hidden',
        )}
      >
        Showing {items.length} of {total}
      </p>
    </>
  )
}
